(ns ui.sticker-dialogs.actions
  "What the action handlers (`:sticker-dialog/*`) of the Replicant
   sticker dialogs share: the imperative shell around
   `ui.sticker-dialogs.model`. The dialog state lives under
   `[:ui/modal :sticker]`, which `:ui/modal-close` drops with the modal.
   Adding a sticker means transacting an asset into the editor2
   DataScript db and adding it as a clip, via the existing `:editor2/*`
   actions.

   Everything specific to one sticker lives in its own namespace (e.g.
   `ui.sticker-dialogs.poll`), handlers included. The generic `init!`
   and `submit!` find that part through the register: the `modal` entry
   of a sticker declares

   - `:sticker/kind` the kind of its `[:sticker-dialog/init kind]`
   - `:sticker/initial-state` `(fn [{:keys [data db]}])`, the state of
     the freshly opened dialog, seeded from the `data` of the sticker it
     edits (nil for a new one)
   - `:sticker/single-asset-type` (optional) the asset type of a sticker
     a story has at most once: opening its dialog without an asset edits
     the existing sticker
   - `:sticker/submit-data` (optional) `(fn [dialog])`, the
     `[asset-type asset-name data]` that `[:sticker-dialog/submit]` adds,
     or nil while the dialog is not valid"
  (:require [datascript.core :as ds]
            [ui.sticker-dialogs.model :as model]))

(def path
  [:ui/modal :sticker])

(defn dialog
  [state]
  (get-in state path))

(defn swap-dialog!
  "Updates the open dialog, a no-op when none is open."
  [store f & args]
  (swap! store
         (fn [state]
           (if (dialog state)
             (apply update-in state path f args)
             state))))

(defn swap-dialog-of!
  "Updates the open dialog only when it is of `kind`: the answer of a
   search or validation must not touch a dialog that was opened
   meanwhile."
  [store kind f & args]
  (swap! store
         (fn [state]
           (if (= kind (:kind (dialog state)))
             (apply update-in state path f args)
             state))))

(defn sticker
  "The register entry of the sticker dialog `kind`."
  [get-register kind]
  (some (fn [entry]
          (when (= kind (:sticker/kind entry))
            entry))
        (get-register)))

(defn existing-asset
  "The asset the dialog edits: the one of the modal params (a double
   click on a sticker, the Edit button of its sidebar), or for a sticker
   with a `single-asset-type` the one already in the story."
  [db single-asset-type params]
  (cond
    (:asset/uuid params)
    (select-keys params [:asset/uuid :db/id :asset/data])

    single-asset-type
    (when-let [asset-id (when db
                          (first (ds/q '[:find [?asset ...]
                                         :in $ ?type
                                         :where
                                         [?asset :asset/type ?type]
                                         [_ :clip/asset ?asset]]
                                       db
                                       single-asset-type)))]
      (let [asset (ds/entity db asset-id)]
        {:asset/uuid (:asset/uuid asset)
         :db/id (:db/id asset)
         :asset/data (:asset/data asset)}))

    :else
    nil))

(defn init!
  "`[:sticker-dialog/init kind]`, the `:on-load` of every dialog."
  [{:keys [store action ui/get-register]}]
  (let [[_ kind] action
        {:sticker/keys [initial-state single-asset-type]} (sticker get-register kind)
        state @store
        params (get-in state [:ui/modal :params])
        db (get-in state [:editor2 :db])
        existing (existing-asset db single-asset-type params)]
    (swap! store
           assoc-in
           path
           (merge {:kind kind
                   :existing existing}
                  (initial-state {:data (:asset/data existing)
                                  :db db})))))

(defn asset-tx
  "The asset entity to transact: a new one, or the edited one."
  [existing asset-type asset-name data]
  {:db/id (or (:db/id existing)
              (str (name asset-type) "-asset"))
   :asset/uuid (or (:asset/uuid existing)
                   (random-uuid))
   :asset/type asset-type
   :asset/name asset-name
   :asset/data data})

(defn asset-actions
  "Transacts the `asset` and, for a new one, adds it as a clip."
  [existing asset]
  (concat [[:editor2/transact-asset [asset]]
           [:editor2/prepare-assets!]]
          (when-not (:asset/uuid existing)
            [[:editor2/add-sticker-asset-as-clip (:asset/uuid asset)]])))

(defn invisible
  [data invisible?]
  (cond-> data
    invisible? (assoc :invisible true)))

(def text-sticker-base
  {:scaleX 1
   :scaleY 1
   :top 0
   :left 0})

(defn submit!
  "`[:sticker-dialog/submit]` of the dialogs that add a single asset,
   the `:sticker/submit-data` of their sticker."
  [{:keys [store ui/event-handler ui/get-register]}]
  (let [{:keys [kind existing] :as dialog} (dialog @store)
        submit-data (:sticker/submit-data (sticker get-register kind))]
    (when-let [[asset-type asset-name data] (when submit-data
                                              (submit-data dialog))]
      (event-handler {}
                     (concat (asset-actions existing
                                            (asset-tx existing asset-type asset-name data))
                             [[:ui/modal-close]])))))

(defn handler
  [kind f]
  {:ui.action/kind kind
   :ui.action/handler f})

(def handlers
  [(handler :sticker-dialog/init init!)
   (handler :sticker-dialog/set-field
            (fn [{:keys [store action]}]
              (let [[_ field value] action]
                (swap-dialog! store model/set-field field value))))
   (handler :sticker-dialog/assoc
            (fn [{:keys [store action]}]
              (let [[_ & kvs] action]
                (apply swap-dialog! store assoc kvs))))
   (handler :sticker-dialog/merge
            (fn [{:keys [store action]}]
              (swap-dialog! store merge (second action))))
   (handler :sticker-dialog/submit submit!)])
